import React, { useEffect, useMemo, useRef, useState } from "react"
import { useQuitGuard } from "../../hooks/useQuitGuard"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye } from "@fortawesome/free-solid-svg-icons"
import {
	fetchRapprochement,
	importBancaire,
	validerRapprochement,
	ignorerLignes,
	desapparier,
	reouvrirIgnoree,
	IMPORT_BANCAIRE_RESET,
} from "../../store/actions/rapprochementActions"
import CustomModal from "../basics/interface/CustomModal"
import AppBtn from "../basics/button/AppBtn"
import Loader from "../basics/Loader"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"

const frDate = (iso) => (iso ? iso.split("-").reverse().join("-") : "")
const fmt = (n) => {
	const v = parseFloat(n)
	return isNaN(v) ? "–" : v.toFixed(2)
}

const FICHE_ROUTES = {
	atelier: (id) => `/atelier/fiche/${id}`,
	location: (id) => `/location/fiche/${id}`,
	depannage: (id) => `/depannage/fiche/${id}`,
}

const TYPE_LABELS = {
	atelier: "Atelier",
	location: "Location",
	depannage: "Dépannage",
}

const Rapprochement = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const fileRef = useRef(null)

	const {
		lignes,
		suggestions,
		transactions,
		appariees,
		ignorees,
		fetchRapprochement: fetchState,
		importBancaire: importState,
		validerRapprochement: validerState,
		ignorerLignes: ignorerState,
	} = useSelector((s) => s.rapprochementInfo)

	const [selections, setSelections] = useState({})
	// Revue 7 (🟠B) : appariements choisis à la main et non validés → navigation
	// confirmée, jamais de rechargement automatique au deploy.
	const { setDirty, setClean, QuitGuard } = useQuitGuard()
	const [showValiderModal, setShowValiderModal] = useState(false)
	const [showIgnorerToutModal, setShowIgnorerToutModal] = useState(false)
	const [manualSearch, setManualSearch] = useState({})
	const [showAppariees, setShowAppariees] = useState(false)
	const [showIgnorees, setShowIgnorees] = useState(false)

	useEffect(() => {
		dispatch(fetchRapprochement())
	}, [dispatch])

	useEffect(() => {
		if (suggestions.fort) {
			const auto = {}
			for (const item of suggestions.fort) {
				auto[item.ligne.id] = item.transaction.id
			}
			setSelections((prev) => ({ ...auto, ...prev }))
		}
	}, [suggestions.fort])

	const handleImport = (e) => {
		const file = e.target.files[0]
		if (file) {
			dispatch(importBancaire(file)).then(() => {
				dispatch(fetchRapprochement())
			})
		}
		if (fileRef.current) fileRef.current.value = ""
	}

	const handleSelect = (ligneId, txId) => {
		setDirty()
		setSelections((prev) => {
			if (prev[ligneId] === txId) {
				const next = { ...prev }
				delete next[ligneId]
				return next
			}
			return { ...prev, [ligneId]: txId }
		})
	}

	const selectedPairs = useMemo(() => {
		return Object.entries(selections).map(([ligneId, txId]) => ({
			ligne_id: parseInt(ligneId),
			transaction_id: txId,
		}))
	}, [selections])

	const handleValider = () => {
		if (selectedPairs.length === 0) return
		dispatch(validerRapprochement(selectedPairs)).then(() => {
			dispatch(fetchRapprochement())
			setSelections({})
			setClean()
		})
		setShowValiderModal(false)
	}

	const handleIgnorer = (ligneIds) => {
		dispatch(ignorerLignes(ligneIds)).then(() => {
			dispatch(fetchRapprochement()).then(() => {
				setSelections((prev) => {
					const next = { ...prev }
					for (const id of ligneIds) delete next[id]
					return next
				})
			})
		})
	}

	const handleDesapparier = (ligneId) => {
		dispatch(desapparier(ligneId)).then(() => {
			dispatch(fetchRapprochement()).then(() => {
				setSelections((prev) => {
					const next = { ...prev }
					delete next[ligneId]
					return next
				})
			})
		})
	}

	const handleReouvrir = (ligneId) => {
		dispatch(reouvrirIgnoree(ligneId)).then(() => {
			dispatch(fetchRapprochement())
		})
	}

	const toggleManualSearch = (ligneId) => {
		setManualSearch((prev) => ({
			...prev,
			[ligneId]: !prev[ligneId],
		}))
	}

	const allTxList = useMemo(() => Object.values(transactions), [transactions])

	const orphelineIds = useMemo(
		() =>
			suggestions.orpheline
				? suggestions.orpheline.map((item) => item.ligne.id)
				: [],
		[suggestions.orpheline],
	)

	const handleIgnorerTout = () => {
		if (orphelineIds.length === 0) return
		handleIgnorer(orphelineIds)
		setShowIgnorerToutModal(false)
	}

	if (fetchState.loading && lignes.length === 0) {
		return (
			<div style={gs.page}>
				<Loader />
			</div>
		)
	}

	return (
		<div style={gs.page}>
			{QuitGuard}
			<div style={gs.header}>
				<div>
					<div style={gs.title}>Rapprochement bancaire</div>
					<div
						style={{
							color: COLORS.textMuted,
							fontSize: 13,
							marginTop: 4,
						}}
					>
						Import CSV e-banking et appariement des paiements
					</div>
				</div>
				<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
					<AppBtn
						title="Rafraîchir"
						styleName="secondary"
						onClick={() => dispatch(fetchRapprochement())}
					/>
					<input
						ref={fileRef}
						type="file"
						accept=".csv"
						style={{ display: "none" }}
						onChange={handleImport}
					/>
					<AppBtn
						title="Importer un CSV"
						styleName="primary"
						onClick={() =>
							fileRef.current && fileRef.current.click()
						}
						loading={importState.saving}
					/>
				</div>
			</div>

			{importState.saved && importState.result && (
				<div
					style={{
						...gs.badge(COLORS.success, COLORS.successLight),
						marginBottom: 16,
						padding: "10px 16px",
						fontSize: 13,
					}}
				>
					Import : {importState.result.nouvelles} nouvelle(s),{" "}
					{importState.result.connues} déjà connue(s) sur{" "}
					{importState.result.total} lignes.
					<span
						style={{
							marginLeft: 8,
							cursor: "pointer",
							opacity: 0.7,
						}}
						onClick={() =>
							dispatch({ type: IMPORT_BANCAIRE_RESET })
						}
					>
						✕
					</span>
				</div>
			)}

			{suggestions.fort && suggestions.fort.length > 0 && (
				<Section
					title="Correspondances exactes"
					count={suggestions.fort.length}
					color={COLORS.success}
				>
					<div style={tableStyles.container}>
						<table style={tableStyles.table}>
							<thead>
								<tr>
									<th style={tableStyles.th}>Date</th>
									<th style={tableStyles.th}>
										Texte bancaire
									</th>
									<th style={tableStyles.thRight}>Montant</th>
									<th style={tableStyles.th}>Facture</th>
									<th style={tableStyles.th}>Entité</th>
									<th
										style={{
											...tableStyles.th,
											textAlign: "center",
										}}
									>
										Valider
									</th>
								</tr>
							</thead>
							<tbody>
								{suggestions.fort.map((item, i) => {
									const tx =
										transactions[item.transaction.id] ||
										item.transaction
									const checked =
										selections[item.ligne.id] === tx.id
									return (
										<LigneRow
											key={item.ligne.id}
											ligne={item.ligne}
											tx={tx}
											index={i}
											checked={checked}
											onToggle={() =>
												handleSelect(
													item.ligne.id,
													tx.id,
												)
											}
											onOpen={(route) =>
												history.push(route)
											}
										/>
									)
								})}
							</tbody>
						</table>
					</div>
				</Section>
			)}

			{suggestions.ambigu && suggestions.ambigu.length > 0 && (
				<Section
					title="Correspondances multiples"
					count={suggestions.ambigu.length}
					color={COLORS.warning}
				>
					{suggestions.ambigu.map((item) => (
						<AmbiguCard
							key={item.ligne.id}
							item={item}
							transactions={transactions}
							selected={selections[item.ligne.id]}
							onSelect={(txId) =>
								handleSelect(item.ligne.id, txId)
							}
							onIgnore={() => handleIgnorer([item.ligne.id])}
							onOpen={(route) => history.push(route)}
						/>
					))}
				</Section>
			)}

			{suggestions.orpheline && suggestions.orpheline.length > 0 && (
				<Section
					title="Sans correspondance"
					count={suggestions.orpheline.length}
					color={COLORS.textMuted}
					action={
						<AppBtn
							title="Tout ignorer"
							styleName="danger"
							onClick={() => setShowIgnorerToutModal(true)}
						/>
					}
				>
					<div style={tableStyles.container}>
						<table style={tableStyles.table}>
							<thead>
								<tr>
									<th style={tableStyles.th}>Date</th>
									<th style={tableStyles.th}>
										Texte bancaire
									</th>
									<th style={tableStyles.thRight}>Montant</th>
									<th style={tableStyles.th}>Recherche</th>
									<th
										style={{
											...tableStyles.th,
											textAlign: "center",
										}}
									>
										Action
									</th>
								</tr>
							</thead>
							<tbody>
								{suggestions.orpheline.map((item, i) => (
									<OrphelineRow
										key={item.ligne.id}
										ligne={item.ligne}
										index={i}
										manualOpen={manualSearch[item.ligne.id]}
										toggleManual={() =>
											toggleManualSearch(item.ligne.id)
										}
										allTx={allTxList}
										selected={selections[item.ligne.id]}
										onSelect={(txId) =>
											handleSelect(item.ligne.id, txId)
										}
										onIgnore={() =>
											handleIgnorer([item.ligne.id])
										}
										onOpen={(route) => history.push(route)}
									/>
								))}
							</tbody>
						</table>
					</div>
				</Section>
			)}

			{appariees && appariees.length > 0 && (
				<div style={{ marginBottom: 24 }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 10,
							marginBottom: 12,
							cursor: "pointer",
						}}
						onClick={() => setShowAppariees((v) => !v)}
					>
						<span
							style={{
								fontSize: 16,
								fontWeight: 600,
								color: COLORS.text,
							}}
						>
							Appariées récemment
						</span>
						<span style={gs.badge(COLORS.textMuted, COLORS.bg)}>
							{appariees.length}
						</span>
						<span style={{ fontSize: 12, color: COLORS.textLight }}>
							{showAppariees ? "▲" : "▼"}
						</span>
					</div>
					{showAppariees && (
						<div style={tableStyles.container}>
							<table style={tableStyles.table}>
								<thead>
									<tr>
										<th style={tableStyles.th}>Date</th>
										<th style={tableStyles.th}>
											Texte bancaire
										</th>
										<th style={tableStyles.thRight}>
											Montant
										</th>
										<th style={tableStyles.th}>
											Facture liée
										</th>
										<th style={tableStyles.th}>
											Validé le
										</th>
										<th
											style={{
												...tableStyles.th,
												textAlign: "center",
											}}
										>
											Action
										</th>
									</tr>
								</thead>
								<tbody>
									{appariees.map((item, i) => {
										const bg = tableStyles.rowBg(i)
										const tx = item.transaction
										const route =
											tx && tx.fiche_type && tx.fiche_id
												? FICHE_ROUTES[tx.fiche_type]
												: null
										return (
											<tr
												key={item.id}
												style={{ backgroundColor: bg }}
											>
												<td style={tableStyles.td}>
													{frDate(
														item.date_comptable,
													)}
												</td>
												<td
													style={{
														...tableStyles.td,
														maxWidth: 300,
														overflow: "hidden",
														textOverflow:
															"ellipsis",
													}}
												>
													{item.texte}
												</td>
												<td style={tableStyles.tdRight}>
													{fmt(item.montant)}
												</td>
												<td style={tableStyles.td}>
													{tx && route ? (
														<span
															style={{
																color: COLORS.accent,
																cursor: "pointer",
															}}
															onClick={() =>
																history.push(
																	route(
																		tx.fiche_id,
																	),
																)
															}
														>
															{
																TYPE_LABELS[
																	tx
																		.fiche_type
																]
															}{" "}
															#{tx.fiche_id} ·{" "}
															{tx.entity_name}
														</span>
													) : (
														"–"
													)}
												</td>
												<td style={tableStyles.td}>
													{item.valide_le || "–"}
												</td>
												<td
													style={{
														...tableStyles.td,
														textAlign: "center",
													}}
												>
													<AppBtn
														title="Dé-apparier"
														styleName="danger"
														onClick={() =>
															handleDesapparier(
																item.id,
															)
														}
													/>
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{ignorees && ignorees.length > 0 && (
				<div style={{ marginBottom: 24 }}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 10,
							marginBottom: 12,
							cursor: "pointer",
						}}
						onClick={() => setShowIgnorees((v) => !v)}
					>
						<span
							style={{
								fontSize: 16,
								fontWeight: 600,
								color: COLORS.text,
							}}
						>
							Ignorées récemment
						</span>
						<span style={gs.badge(COLORS.textMuted, COLORS.bg)}>
							{ignorees.length}
						</span>
						<span style={{ fontSize: 12, color: COLORS.textLight }}>
							{showIgnorees ? "▲" : "▼"}
						</span>
					</div>
					{showIgnorees && (
						<div style={tableStyles.container}>
							<table style={tableStyles.table}>
								<thead>
									<tr>
										<th style={tableStyles.th}>Date</th>
										<th style={tableStyles.th}>
											Texte bancaire
										</th>
										<th style={tableStyles.thRight}>
											Montant
										</th>
										<th style={tableStyles.th}>
											Ignoré le
										</th>
										<th
											style={{
												...tableStyles.th,
												textAlign: "center",
											}}
										>
											Action
										</th>
									</tr>
								</thead>
								<tbody>
									{ignorees.map((item, i) => {
										const bg = tableStyles.rowBg(i)
										return (
											<tr
												key={item.id}
												style={{
													backgroundColor: bg,
												}}
											>
												<td style={tableStyles.td}>
													{frDate(
														item.date_comptable,
													)}
												</td>
												<td
													style={{
														...tableStyles.td,
														maxWidth: 300,
														overflow: "hidden",
														textOverflow:
															"ellipsis",
													}}
												>
													{item.texte}
												</td>
												<td style={tableStyles.tdRight}>
													{fmt(item.montant)}
												</td>
												<td style={tableStyles.td}>
													{item.valide_le || "–"}
												</td>
												<td
													style={{
														...tableStyles.td,
														textAlign: "center",
													}}
												>
													<AppBtn
														title="Ré-ouvrir"
														styleName="secondary"
														onClick={() =>
															handleReouvrir(
																item.id,
															)
														}
													/>
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			{lignes.length === 0 && !fetchState.loading && (
				<div
					style={{
						textAlign: "center",
						padding: 48,
						color: COLORS.textMuted,
					}}
				>
					Aucune ligne bancaire à rapprocher. Importez un relevé CSV.
				</div>
			)}

			{selectedPairs.length > 0 && (
				<div style={styles.bottomBar}>
					<span style={{ fontSize: 14, fontWeight: 500 }}>
						{selectedPairs.length} appariement(s) sélectionné(s)
					</span>
					<AppBtn
						title="Valider le rapprochement"
						styleName="primary"
						onClick={() => setShowValiderModal(true)}
					/>
				</div>
			)}

			{showValiderModal && (
				<CustomModal
					title="Confirmer le rapprochement"
					onClose={() => setShowValiderModal(false)}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setShowValiderModal(false),
						},
						{
							title: "Confirmer",
							styleName: "primary",
							onClick: handleValider,
							loading: validerState.saving,
						},
					]}
				>
					<p style={{ fontSize: 14, color: COLORS.text }}>
						{selectedPairs.length} transaction(s) seront datées à la
						date comptable du paiement. Cette action est réversible
						(dé-appariement).
					</p>
				</CustomModal>
			)}

			{showIgnorerToutModal && (
				<CustomModal
					title="Ignorer les sans correspondance"
					onClose={() => setShowIgnorerToutModal(false)}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setShowIgnorerToutModal(false),
						},
						{
							title: "Tout ignorer",
							styleName: "danger",
							onClick: handleIgnorerTout,
							loading: ignorerState.saving,
						},
					]}
				>
					<p style={{ fontSize: 14, color: COLORS.text }}>
						{orphelineIds.length} ligne(s) bancaire(s) sans
						correspondance seront ignorées et n'apparaîtront plus.
						Un ré-import du même relevé ne les fera pas revenir.
					</p>
				</CustomModal>
			)}
		</div>
	)
}

const Section = ({ title, count, color, children, action }) => (
	<div style={{ marginBottom: 24 }}>
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 10,
				marginBottom: 12,
			}}
		>
			<span style={{ fontSize: 16, fontWeight: 600, color: COLORS.text }}>
				{title}
			</span>
			<span style={gs.badge(color, COLORS.bg)}>{count}</span>
			{action && <div style={{ marginLeft: "auto" }}>{action}</div>}
		</div>
		{children}
	</div>
)

const LigneRow = ({ ligne, tx, index, checked, onToggle, onOpen }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const route =
		tx.fiche_type && tx.fiche_id ? FICHE_ROUTES[tx.fiche_type] : null

	return (
		<tr
			onClick={onToggle}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={tableStyles.td}>{frDate(ligne.date_comptable)}</td>
			<td
				style={{
					...tableStyles.td,
					maxWidth: 300,
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{ligne.texte}
			</td>
			<td style={tableStyles.tdRight}>{fmt(ligne.montant)}</td>
			<td style={tableStyles.td}>
				{route ? (
					<span
						style={{ color: COLORS.accent, cursor: "pointer" }}
						onClick={(e) => {
							e.stopPropagation()
							onOpen(route(tx.fiche_id))
						}}
					>
						{TYPE_LABELS[tx.fiche_type]} #{tx.fiche_id}
					</span>
				) : (
					"–"
				)}
			</td>
			<td style={tableStyles.td}>{tx.entity_name || "–"}</td>
			<td style={{ ...tableStyles.td, textAlign: "center" }}>
				<input type="checkbox" checked={checked} readOnly />
			</td>
		</tr>
	)
}

const AmbiguCard = ({
	item,
	transactions,
	selected,
	onSelect,
	onIgnore,
	onOpen,
}) => {
	const ligne = item.ligne
	return (
		<div style={{ ...tableStyles.container, padding: 16, marginBottom: 8 }}>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					marginBottom: 10,
				}}
			>
				<div>
					<span style={{ fontWeight: 600, fontSize: 14 }}>
						{frDate(ligne.date_comptable)}
					</span>
					<span style={{ margin: "0 12px", color: COLORS.textMuted }}>
						|
					</span>
					<span style={{ fontSize: 14 }}>{ligne.texte}</span>
					<span style={{ margin: "0 12px", color: COLORS.textMuted }}>
						|
					</span>
					<span style={{ fontWeight: 600, fontSize: 14 }}>
						{fmt(ligne.montant)}
					</span>
				</div>
				<AppBtn title="Ignorer" styleName="danger" onClick={onIgnore} />
			</div>
			<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
				{item.transactions.map((t) => {
					const tx = transactions[t.transaction.id] || t.transaction
					const isSelected = selected === tx.id
					const route =
						tx.fiche_type && tx.fiche_id
							? FICHE_ROUTES[tx.fiche_type]
							: null
					return (
						<div
							key={tx.id}
							onClick={() => onSelect(tx.id)}
							style={{
								padding: "8px 14px",
								borderRadius: 8,
								border: `1.5px solid ${isSelected ? COLORS.accent : COLORS.border}`,
								backgroundColor: isSelected
									? COLORS.accentLight
									: COLORS.surface,
								cursor: "pointer",
								fontSize: 13,
								transition: "all 0.15s",
							}}
						>
							<div style={{ fontWeight: 500 }}>
								{tx.entity_name || "–"}
							</div>
							<div
								style={{
									color: COLORS.textMuted,
									fontSize: 12,
								}}
							>
								{fmt(tx.total)} · {tx.details}
								{route && (
									<span
										style={{
											color: COLORS.accent,
											marginLeft: 6,
											cursor: "pointer",
										}}
										onClick={(e) => {
											e.stopPropagation()
											onOpen(route(tx.fiche_id))
										}}
									>
										{TYPE_LABELS[tx.fiche_type]} #
										{tx.fiche_id}
									</span>
								)}
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}

const OrphelineRow = ({
	ligne,
	index,
	manualOpen,
	toggleManual,
	allTx,
	selected,
	onSelect,
	onIgnore,
	onOpen,
}) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)

	return (
		<>
			<tr
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
				style={{
					backgroundColor: bg,
					transition: "background-color 0.12s",
				}}
			>
				<td style={tableStyles.td}>{frDate(ligne.date_comptable)}</td>
				<td
					style={{
						...tableStyles.td,
						maxWidth: 300,
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{ligne.texte}
				</td>
				<td style={tableStyles.tdRight}>{fmt(ligne.montant)}</td>
				<td style={tableStyles.td}>
					<AppBtn
						title={manualOpen ? "Fermer" : "Rechercher"}
						styleName="secondary"
						onClick={toggleManual}
					/>
				</td>
				<td style={{ ...tableStyles.td, textAlign: "center" }}>
					<AppBtn
						title="Ignorer"
						styleName="danger"
						onClick={onIgnore}
					/>
				</td>
			</tr>
			{manualOpen && (
				<tr>
					<td
						colSpan={5}
						style={{
							padding: "8px 16px",
							backgroundColor: COLORS.bg,
							border: "none",
						}}
					>
						<div style={{ maxHeight: 200, overflowY: "auto" }}>
							{allTx.length === 0 ? (
								<div
									style={{
										color: COLORS.textMuted,
										fontSize: 13,
										padding: 8,
									}}
								>
									Aucune transaction disponible.
								</div>
							) : (
								allTx.map((tx) => {
									const isSelected = selected === tx.id
									const route =
										tx.fiche_type && tx.fiche_id
											? FICHE_ROUTES[tx.fiche_type]
											: null
									return (
										<div
											key={tx.id}
											onClick={() => onSelect(tx.id)}
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												padding: "6px 10px",
												borderRadius: 6,
												cursor: "pointer",
												backgroundColor: isSelected
													? COLORS.accentLight
													: "transparent",
												border: isSelected
													? `1px solid ${COLORS.accent}`
													: "1px solid transparent",
												fontSize: 13,
												marginBottom: 2,
											}}
										>
											<span>
												{tx.entity_name || "–"} ·{" "}
												{fmt(tx.total)} · {tx.details}
											</span>
											{route && (
												<button
													style={gs.iconBtn(
														COLORS.accent,
													)}
													onClick={(e) => {
														e.stopPropagation()
														onOpen(
															route(tx.fiche_id),
														)
													}}
													title="Ouvrir la fiche"
												>
													<FontAwesomeIcon
														icon={faEye}
													/>
												</button>
											)}
										</div>
									)
								})
							)}
						</div>
					</td>
				</tr>
			)}
		</>
	)
}

const styles = {
	bottomBar: {
		position: "fixed",
		bottom: 0,
		left: 0,
		right: 0,
		backgroundColor: COLORS.surface,
		borderTop: `1px solid ${COLORS.border}`,
		padding: "12px 24px",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		zIndex: 100,
		boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
	},
}

export default Rapprochement
